import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { storybookGateCopy } from '@/lib/paywall';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { StorybookReader, storybookPageAudio, type StorybookPageData } from '@/components/storybooks/StorybookReader';
import { localStorybooks } from '@/content/mobile';
import { localResources } from '@/content/resources';
import { useCatalogResource } from '@/hooks/useLearnerCatalog';
import { learnerApiJson } from '@/lib/learner-api';
import { isPremiumRequiredError, shouldHidePremiumContent } from '@/lib/premium-gate';
import { enqueueActivity } from '@/lib/progress-queue';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';
import { playAudio } from '@/lib/play-audio';

type CloudStory = {
  name: string;
  requires_subscription: boolean;
  pages: Array<{
    image_url: string | null;
    image_tablet_url: string | null;
    sound_url: string | null;
    sound_children_url: string | null;
  }>;
};

export const Route = createFileRoute('/app/storybooks/$slug')({ component: StorybookPage });

const localKeys: Record<string, keyof typeof localStorybooks> = {
  halloween: 'halloweenPages',
  mai: 'maiStorybookPages',
  alphabet: 'alphabetStorybookPages',
  'hmong-meaning': 'hmongMeaningStorybookPages',
  'kuv-sov-sov': 'kuvSovSovPhonePages',
  'sky-high': 'skyHighPages',
};

export function StorybookPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const preferences = useLearnerPreferences();
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const mode = preferences.dialect === 'moob' ? 'moob' : 'hmonglus';
  const catalogResource = useCatalogResource(
    '/v1/storybooks',
    mode,
    preferences.baseLanguage,
    'Storybooks',
    '/app/storybooks',
    slug,
  );
  const cloud = useQuery({
    queryKey: ['storybook', slug],
    queryFn: () => learnerApiJson<CloudStory>(auth?.getToken ?? null, `/v1/storybooks/${encodeURIComponent(slug)}`),
    retry: false,
  });
  const localPages = useMemo<StorybookPageData[]>(() => {
    const key = slug === 'mai' && preferences.dialect === 'moob' ? 'maiMoobStorybookPages' : localKeys[slug];
    return key
      ? localStorybooks[key].map((page) => ({
          image: page.image ?? null,
          sound: page.sound ?? null,
          soundChildren: page.soundChildren ?? null,
        }))
      : [];
  }, [preferences.dialect, slug]);
  const paymentRequired = isPremiumRequiredError(cloud.error);
  const localResource = localResources.find((item) => item.href.endsWith(`/storybooks/${slug}`));
  const gated = shouldHidePremiumContent({
    flaggedPremium: Boolean(
      catalogResource?.premium || localResource?.premium || cloud.data?.requires_subscription,
    ),
    paymentRequired,
    isSubscribed: premium.isSubscribed,
  });
  const pages: StorybookPageData[] = gated
    ? []
    : cloud.data?.pages.map((page) => ({
        image: page.image_url || page.image_tablet_url,
        sound: page.sound_url,
        soundChildren: page.sound_children_url,
      })) ?? localPages;
  const [index, setIndex] = useState(0);
  const page = pages[index];

  useEffect(() => {
    setIndex(0);
    track('Storybook Screen', { storybook: slug });
  }, [slug]);

  if (gated) {
    const gate = storybookGateCopy(slug);
    return (
      <SubscriptionGate
        source={gate.source}
        content={slug}
        message={gate.message}
        heroEmoji={gate.heroEmoji}
        onDismiss={() => void navigate({ to: '/app/resources' })}
      />
    );
  }
  if (!pages.length) return <main className="grid min-h-[75vh] place-items-center">Loading story…</main>;

  const move = (next: number) => {
    setIndex(next);
    if (next === pages.length - 1) {
      void enqueueActivity('storybook_completion', { storybook: slug });
      track('Storybook Completed', { storybook: slug });
    }
  };

  const play = () => {
    const source = storybookPageAudio(page, preferences.voice);
    if (source) void playAudio(source);
    track('Storybook Speaker Press', { storybook: slug, page: index + 1 });
  };

  return (
    <StorybookReader
      pages={pages}
      index={index}
      onIndexChange={move}
      onPlayAudio={play}
      onExit={() => void navigate({ to: '/app/resources' })}
    />
  );
}
